// Zentrale Inhaltsstruktur — alle Texte der Seite, zweisprachig (DE Standard / EN).
// Positionierung: Projektmanagement · Produktion · Produktentwicklung · Prepress.
// Bilder der Fallstudien: "plates" — src + zweisprachige Bildunterschriften; pflegbar im Adminbereich.

export const content = {
  de: {
    links: {
      email: "work@jolenz.de",
      artUrl: "https://jolenz.art",
      linkedinUrl: "https://www.linkedin.com/in/jo-lenz/",
      instagramUrl: "https://instagram.com/jolenzart",
    },
    meta: {
      title: "Jo Lenz — Projektmanagement, Produktion, Produktentwicklung & Prepress",
      description:
        "Jo Lenz verbindet Gestaltungserfahrung mit Produktionsverständnis, Organisation und kaufmännischer Praxis. Projektmanagement, Print/Prepress und Produktentwicklung. Berlin — Teilzeit oder Vollzeit.",
    },
    nav: {
      tagline: "Projektmanagement · Produktion · Prepress",
      items: [
        { label: "Einsatzbereiche", href: "#einsatz", testid: "nav-einsatz" },
        { label: "Projekte", href: "#arbeiten", testid: "nav-cases" },
        { label: "Erfahrung", href: "#erfahrung", testid: "nav-experience" },
        { label: "Kontakt", href: "#kontakt", testid: "nav-contact" },
      ],
    },
    hero: {
      name: "Jo Lenz",
      positioning: "Projektmanagement · Produktion · Produktentwicklung · Prepress",
      seeking: "Ich suche eine Position, in der ich Dinge nicht nur plane, sondern mit dafür sorge, dass sie wirklich fertig werden.",
      availability: "Berlin · Teilzeit oder Vollzeit · vor Ort / hybrid / remote",
      lines: ["Ich könnte hier", "ziemlich nützlich sein."],
      sub: "Ich verbinde Gestaltungserfahrung mit Produktionsverständnis, Organisation und kaufmännischer Praxis. Besonders gern arbeite ich an der Schnittstelle zwischen Idee, Daten, Menschen und fertigem Produkt.",
      ctaCv: "Lebenslauf ansehen",
      ctaContact: "Kontakt",
      meta: ["Berlin / Brandenburg", "DE / EN"],
      ghost: "nützlich",
      plateAwaiting: "Foto folgt",
    },
    einsatz: {
      chapter: "Kapitel 01",
      label: "Einsatzbereiche",
      headline: "Wo ich gut einsetzbar bin",
      areas: [
        {
          title: "Projekt- & Produktionskoordination",
          text: "Abläufe strukturieren, Beteiligte koordinieren, Termine und offene Punkte im Blick behalten.",
        },
        {
          title: "Print, Prepress & Packaging",
          text: "Druckdaten, Reinzeichnung, Verpackung, Etiketten, Produktionsanforderungen und Abstimmung mit Druckereien und Lieferanten.",
        },
        {
          title: "Produktentwicklung & Umsetzung",
          text: "Vom Entwurf über Material, Prototyp und Produktion bis zum fertigen physischen Produkt.",
        },
        {
          title: "Organisation & kaufmännische Praxis",
          text: "Angebote, Einkauf, Rechnungen, Bestände, Lieferanten, Kundenkommunikation und kaufmännische Abläufe.",
        },
      ],
    },
    cases: {
      chapter: "Kapitel 02",
      label: "Projekte",
      headline: "Nicht nur behauptet. Gemacht.",
      intro: "Drei Stationen, drei Felder — dasselbe Muster: verstehen, vorbereiten, koordinieren, produzieren, fertigstellen.",
      workLabel: "Aufgaben",
      platesLabel: "Druckplatten · Fotos folgen",
      galleryLabel: "Belege aus der Produktion",
      enlargeLabel: "Vergrößern",
      closeLabel: "Schließen",
      prevLabel: "Vorheriges Bild",
      nextLabel: "Nächstes Bild",
      items: [
        {
          id: "kaiser",
          num: "01",
          name: "Kaiserdisplay",
          claim: "POS · Packaging · Produktion · Projektsteuerung",
          tag: "Projekt- & Produktionssteuerung",
          body: [
            "Langjährige Tätigkeit in der Projekt- und Produktionssteuerung für POS-, Promotion- und Verpackungslösungen, später als Gesellschafter-Geschäftsführerin. Verantwortung unter anderem für Kundenprojekte, Produktionskoordination, Beschaffung, Material- und Bestandsplanung sowie die Abstimmung mit Lieferanten, Agenturen und Produktionspartnern.",
            "Dazu kamen die Gestaltung von Markenauftritten, Etiketten und Verpackungen, eigene Druckvorlagen und die Begleitung der Produktion bis zum fertigen Display oder Packaging.",
          ],
          work: ["Kundenprojekte & Projektsteuerung", "Produktionskoordination", "Beschaffung, Material- & Bestandsplanung", "Etiketten, Verpackungen, Markenauftritte", "Abstimmung mit Lieferanten, Agenturen & Produktionspartnern", "Druckdaten-Prüfung"],
          proof: "Verantwortung für Projekte, Kunden und Produktion — vom Angebot bis zur Auslieferung.",
          plates: [],
          link: null,
        },
        {
          id: "ziegel",
          num: "02",
          name: "Ziegel Braumanufaktur",
          claim: "Branding · Packaging · Prepress",
          tag: "Branding · Verpackung · Druckproduktion",
          body: [
            "Für eine lokale Brauerei in Zehdenick habe ich die visuelle Identität und das Verpackungskonzept entwickelt — mit einer starken Verbindung zur Region.",
            "Zentrale Bildidee wurde der historische Ziegelkahn: die Lastkähne, mit denen Ziegel aus der Region Zehdenick Richtung Berlin transportiert wurden. Die Typografie greift den materiellen Charakter von Ziegeln und das industrielle Erbe auf. Von mir kamen Konzept, Etiketten, Brand Book, Banner — und die druckfertigen Daten.",
          ],
          work: ["Branding-Konzept", "Logo & visuelles System", "Flaschenetiketten & Biersorten", "Typografie & Illustration", "Brand Book & Außenbanner", "druckfertige Reinzeichnung", "Abstimmung mit der Druckproduktion"],
          proof: "Die gelieferten Druckdaten liefen in der Produktion — ohne wiederholte Korrekturen oder Rückfragen der Druckerei.",
          plates: [
            { label: "Flaschen: Helles & Pils", src: "/images/ziegel/bottles-grass.webp" },
            { label: "Etiketten-Artwork „Saison“ (flach)", src: "/images/ziegel/label-flat.png" },
            { label: "Außenbanner (Reinzeichnung)", src: "/images/ziegel/banner.webp" },
          ],
          link: null,
        },
        {
          id: "jolenz",
          num: "03",
          name: "Jo Lenz Art",
          claim: "Produktentwicklung · Produktion · Verkauf",
          tag: "Eigene Produktlinie · Direktverkauf",
          body: [
            "Ich entwickle meine eigene physische Produktreihe auf Basis meiner künstlerischen Arbeit: Kunstdrucke, Postkarten, Papeterie, Sticker, Lesezeichen, Verpackungen, Textilien — und die Präsentation am Marktstand.",
            "Der Weg führt von der Idee über Entwurf, Prototyp, Dateiaufbau, Materialwahl und Produktion bis zur Konfektion und zum Kunden. Einige Produkte fertige ich selbst, andere entstehen bei externen Dienstleistern. Ich experimentiere mit Papieren, Druckverfahren, Kaschierungen und Veredelungen — und verkaufe direkt. Dadurch bekomme ich sofortiges Feedback darüber, was als physisches Produkt funktioniert.",
          ],
          work: ["Idee → Entwurf → Prototyp", "Dateiaufbau & Materialwahl", "Produktion & Konfektion", "Verpackungsentwicklung", "Marktpräsentation", "Direktverkauf & Kundenfeedback"],
          proof: "Ich gestalte nicht nur Bilder. Ich mache aus Ideen Produkte — und bringe sie bis zum Kunden.",
          plates: [
            { label: "Marktstand (Übersicht)", src: "/images/jolenz/market-stand.webp", ratio: "aspect-[16/10]", pos: "50% 62%" },
            { label: "Papeterie: Briefpapier-Set", src: "/images/jolenz/papeterie-set.webp" },
            { label: "Lesezeichen: Hologramm-Kaschierung & Quaste", src: "/images/jolenz/bookmark-closeup.jpeg" },
            { label: "Handgemachte Sticker im Verkauf", src: "/images/jolenz/stickers.webp" },
          ],
          link: { label: "Künstlerische Arbeit ansehen", url: "https://jolenz.art" },
        },
      ],
    },
    moreProjects: {
      chapter: "Kapitel 03",
      label: "Weitere Projekte",
      headline: "Auch das gehört dazu.",
      items: [
        {
          title: "Max Ball — Kinderbuch & eigener Verlag",
          text: "Kinderbuchprojekt mit eigenem Kleinverlag: Verlagsprozesse gelernt, Produktion, Vertrieb und Buchhandels-Listung organisiert, Messeauftritte und Mitmachaktionen entwickelt, fair gehandelte, handgenähte Fußbälle zur Buchfigur produziert. Über 300 verkaufte Bücher in den ersten drei bis vier Monaten.",
          image: "/images/maxball/cover.webp",
          imageLabel: "Buchcover „Max Ball — Im Abseits durch Europa“",
        },
        {
          title: "„Es war einmal eine Laterne“",
          text: "Buchprojekt zugunsten der Fluthilfe — Text und Satz (InDesign) von mir, die Druckproduktion hat eine Druckerei gespendet, der Erlös wurde gespendet.",
          image: "",
        },
        {
          title: "Charity-Kunstauktionen",
          text: "Interaktive Online-Auktionen mit fortlaufender Geschichte und individuell gestalteten Avataren; zwei Auktionen erbrachten ca. 750 € bzw. 450 € für ein Katzenhilfe-Projekt.",
          image: "",
        },
      ],
    },
    howIWork: {
      chapter: "Kapitel 04",
      label: "Arbeitsweise",
      headline: "Wie ich arbeite",
      statements: [
        { title: "Ich muss nicht alles schon können, bevor ich anfange.", text: "Ich arbeite mich schnell in Prozesse ein, frage nach, wenn Informationen fehlen, und übernehme dann Verantwortung für meinen Bereich." },
        { title: "Wenn ich einen Ablauf verstanden habe, erkenne ich Zusammenhänge.", text: "Mögliche Probleme und praktikable Lösungen inklusive." },
        { title: "Ich arbeite gern selbstständig.", text: "Aber genauso gern innerhalb klarer Strukturen und Vorgaben." },
      ],
    },
    experience: {
      chapter: "Kapitel 05",
      label: "Erfahrung",
      headline: "Berufserfahrung",
      items: [
        { date: "SEIT 2004", title: "Freie Autorin & Künstlerin", org: "seit 2026 selbstständig unter Jo Lenz Art", note: "Entwicklung, Produktion und Vermarktung eigener Kunst- und Designprodukte — von der Idee bis zum verkaufsfähigen Ergebnis." },
        { date: "BIS 01/2014", title: "Projekt- und Produktionssteuerung, später Gesellschafter-Geschäftsführerin", org: "Kaiserdisplay", note: "POS-, Promotion- und Verpackungslösungen — Kundenprojekte, Produktionskoordination, Beschaffung sowie Abstimmung mit Lieferanten, Agenturen und Produktionspartnern." },
        { date: "2013–2020", title: "Set Nanny / Child Supervisor", org: "Filmproduktionen", note: "Verantwortung für Kinder und Familien am Set, internationale Teams, Arbeiten unter Zeitdruck." },
      ],
      weiterLabel: "Weitere Stationen",
      weiter: "Erziehungshelferin an einer Blindenschule (Internat), Kinderkrippe, Inhaberin einer Kinder- und Jugendboutique, Vereinsverwaltung IG Klettern e. V., Hotel Rheinsberg am See, Tätigkeit in einem Notariat.",
      ausbildungLabel: "Ausbildung",
      ausbildung: "Ausbildung zur Bürokauffrau (IHK), Berufsfachschule Königs Wusterhausen — erfolgreich abgeschlossen.",
    },
    tools: {
      chapter: "Kapitel 06",
      label: "Kenntnisse",
      headline: "Tools & Know-how",
      list: ["Adobe Illustrator", "Adobe Photoshop", "Adobe InDesign", "Print Preparation / Prepress", "Packaging", "Produktentwicklung", "Prototyping", "Finishing", "MS Office", "Canva", "Procreate", "Squarespace"],
    },
    contact: {
      chapter: "Kapitel 07",
      label: "Kontakt",
      headline: "Vielleicht können wir etwas miteinander anfangen.",
      routes: [
        {
          key: "employer",
          label: "Für Unternehmen & Arbeitgeber",
          text: "Sie suchen jemanden, der Projekte eigenständig steuert, Produktion versteht und Verantwortung für reale Aufgaben übernimmt?",
          primary: true,
        },
        {
          key: "project",
          label: "Für Projekte",
          text: "Sie haben eine Produkt-, Publikations-, Druck- oder Verpackungsidee, die jemand durch die praktische Umsetzung denken soll?",
          primary: false,
        },
      ],
      emailLabel: "Schreiben Sie mir gern:",
      location: "Jo Lenz · Berlin / Brandenburg",
      artLabel: "jolenz.art",
      artNote: "Künstlerische Website — öffnet in neuem Tab",
      portraitSrc: "/images/portrait.jpg",
      portraitAlt: "Porträt von Jo Lenz",
    },
    cv: {
      requestLabel: "Lebenslauf anfordern",
      title: "Lebenslauf anfordern",
      intro: "Trag deinen Namen und deine E-Mail-Adresse ein — ich sende dir meinen Lebenslauf als PDF zu. Kein Newsletter, keine Werbung.",
      nameLabel: "Name",
      emailLabel: "E-Mail-Adresse",
      companyLabel: "Unternehmen / Organisation",
      optionalNote: "optional",
      submit: "Anfrage senden",
      sending: "Wird gesendet …",
      successTitle: "Danke — das ist angekommen.",
      successText: "Ich melde mich mit dem Lebenslauf per E-Mail bei dir.",
      errorFields: "Bitte gib deinen Namen und eine gültige E-Mail-Adresse an.",
      errorGeneric: "Etwas hat nicht geklappt. Bitte versuch es erneut oder schreib mir direkt an work@jolenz.de.",
      close: "Schließen",
      variantsLabel: "Welchen Lebenslauf möchtest du erhalten?",
      variantMain: "Project & Operations — Projektkoordination, Produktion, Prepress",
      variantPed: "People & Care — Kinder- & Familienbetreuung, Pädagogik",
      variantOffice: "Customer & Office — Kundenservice, Organisation, Verwaltung",
    },
    footer: {
      line: "Projektmanagement · Produktion · Produktentwicklung · Prepress · Berlin / Brandenburg",
      copyright: "© 2026 Jo Lenz",
      madeNote: "Berlin / Brandenburg",
      legal: [
        { label: "Impressum", url: "https://jolenz.art/privacy-policy-and-impressum", testid: "footer-impressum-link" },
        { label: "Datenschutz", url: "https://jolenz.art/privacy-policy-and-impressum", testid: "footer-datenschutz-link" },
      ],
    },
  },

  en: {
    links: {
      email: "work@jolenz.de",
      artUrl: "https://jolenz.art",
      linkedinUrl: "https://www.linkedin.com/in/jo-lenz/",
      instagramUrl: "https://instagram.com/jolenzart",
    },
    meta: {
      title: "Jo Lenz — Project Management, Production, Product Development & Prepress",
      description:
        "Jo Lenz combines design experience with production understanding, organisation and commercial practice. Project management, print/prepress and product development. Berlin — part-time or full-time.",
    },
    nav: {
      tagline: "Project Management · Production · Prepress",
      items: [
        { label: "Capabilities", href: "#einsatz", testid: "nav-einsatz" },
        { label: "Projects", href: "#arbeiten", testid: "nav-cases" },
        { label: "Experience", href: "#erfahrung", testid: "nav-experience" },
        { label: "Contact", href: "#kontakt", testid: "nav-contact" },
      ],
    },
    hero: {
      name: "Jo Lenz",
      positioning: "Project Management · Production · Product Development · Prepress",
      seeking: "I'm looking for a position where I don't just plan things, but help make sure they actually get finished.",
      availability: "Berlin · part-time or full-time · on-site / hybrid / remote",
      lines: ["I could be pretty", "useful around here."],
      sub: "I combine design experience with production understanding, organisation and commercial practice. I particularly like working where an idea, data, people and a finished product meet.",
      ctaCv: "View CV",
      ctaContact: "Contact",
      meta: ["Berlin / Brandenburg", "DE / EN"],
      ghost: "useful",
      plateAwaiting: "Photo to come",
    },
    einsatz: {
      chapter: "Chapter 01",
      label: "Capabilities",
      headline: "Where I'm most useful",
      areas: [
        {
          title: "Project & Production Coordination",
          text: "Structuring processes, coordinating everyone involved, keeping deadlines and open points in view.",
        },
        {
          title: "Print, Prepress & Packaging",
          text: "Print data, final artwork, packaging, labels, production requirements, and coordination with printers and suppliers.",
        },
        {
          title: "Product Development & Execution",
          text: "From draft through material, prototype and production to the finished physical product.",
        },
        {
          title: "Organisation & Commercial Practice",
          text: "Quotations, purchasing, invoicing, stock, suppliers, customer communication and commercial workflows.",
        },
      ],
    },
    cases: {
      chapter: "Chapter 02",
      label: "Projects",
      headline: "Not just claimed. Done.",
      intro: "Three stations, three fields — the same pattern: understand, prepare, coordinate, produce, finish.",
      workLabel: "Scope of work",
      platesLabel: "Print plates · photos to come",
      galleryLabel: "Evidence from production",
      enlargeLabel: "Enlarge",
      closeLabel: "Close",
      prevLabel: "Previous image",
      nextLabel: "Next image",
      items: [
        {
          id: "kaiser",
          num: "01",
          name: "Kaiserdisplay",
          claim: "POS · Packaging · Production · Project Management",
          tag: "Project & production management",
          body: [
            "Many years of project and production management for POS, promotion and packaging solutions — later as shareholder and managing director. Responsibilities included client projects, production coordination, procurement, material and stock planning, and coordination with suppliers, agencies and production partners.",
            "On top of that: designing brand presences, labels and packaging, preparing my own print artwork, and accompanying production through to the finished display or packaging.",
          ],
          work: ["client projects & project management", "production coordination", "procurement, material & stock planning", "labels, packaging, brand presences", "coordination with suppliers, agencies & production partners", "print-data checking"],
          proof: "Responsibility for projects, clients and production — from quotation to delivery.",
          plates: [],
          link: null,
        },
        {
          id: "ziegel",
          num: "02",
          name: "Ziegel Braumanufaktur",
          claim: "Branding · Packaging · Prepress",
          tag: "Branding · Packaging · Print production",
          body: [
            "For a local brewery in Zehdenick I developed the visual identity and packaging concept — with a strong connection to the region.",
            "The central visual idea became the historic Ziegelkahn: the barges that once transported bricks from the Zehdenick region towards Berlin. The typography picks up the material character of bricks and the region's industrial heritage. I delivered the concept, labels, brand book and banner — and the print-ready files.",
          ],
          work: ["branding concept", "logo & visual system", "bottle labels & beer varieties", "typography & illustration", "brand book & outdoor banner", "print-ready artwork", "coordination with print production"],
          proof: "The print files I supplied ran in production — without repeated corrections or queries from the printer.",
          plates: [
            { label: "Bottles: Helles & Pils", src: "/images/ziegel/bottles-grass.webp" },
            { label: "Label artwork “Saison” (flat)", src: "/images/ziegel/label-flat.png" },
            { label: "Outdoor banner (production artwork)", src: "/images/ziegel/banner.webp" },
          ],
          link: null,
        },
        {
          id: "jolenz",
          num: "03",
          name: "Jo Lenz Art",
          claim: "Product Development · Production · Sales",
          tag: "Own product line · Direct sales",
          body: [
            "I develop my own physical product range based on my artwork: art prints, postcards, stationery, stickers, bookmarks, packaging, apparel — and the market presentation itself.",
            "The path runs from idea through design, prototype, file preparation, material choice and production to finishing and the customer. Some products I manufacture personally, others are produced by external suppliers. I experiment with paper stocks, printing methods, laminates and finishing — and I sell directly, which gives me immediate feedback about what works as a physical product.",
          ],
          work: ["idea → design → prototype", "file preparation & material choice", "production & finishing", "packaging development", "market presentation", "direct sales & customer feedback"],
          proof: "I don't just create images. I turn ideas into products — and take them all the way to the customer.",
          plates: [
            { label: "Market stand (overview)", src: "/images/jolenz/market-stand.webp", ratio: "aspect-[16/10]", pos: "50% 62%" },
            { label: "Stationery: letter paper set", src: "/images/jolenz/papeterie-set.webp" },
            { label: "Bookmark: holographic laminate & tassel", src: "/images/jolenz/bookmark-closeup.jpeg" },
            { label: "Handmade stickers on sale", src: "/images/jolenz/stickers.webp" },
          ],
          link: { label: "View the artistic work", url: "https://jolenz.art" },
        },
      ],
    },
    moreProjects: {
      chapter: "Chapter 03",
      label: "Further Projects",
      headline: "This is part of it too.",
      items: [
        {
          title: "Max Ball — children's book & own publishing house",
          text: "A children's book project with my own small publishing operation: learned the publishing processes, organised production, distribution and bookselling listings, developed trade-fair appearances and hands-on activities, and produced fair-traded, hand-stitched footballs based on the book character. More than 300 books sold within the first three to four months.",
          image: "/images/maxball/cover.webp",
          imageLabel: "Book cover “Max Ball — Im Abseits durch Europa”",
        },
        {
          title: "“Es war einmal eine Laterne”",
          text: "A book project in support of flood relief — I wrote the text and created the layout in InDesign; a printer donated the production and the proceeds were donated.",
          image: "",
        },
        {
          title: "Charity art auctions",
          text: "Interactive online auctions with an evolving story and individually created avatars; two auctions raised roughly €750 and €450 respectively for a cat welfare project.",
          image: "",
        },
      ],
    },
    howIWork: {
      chapter: "Chapter 04",
      label: "Working Style",
      headline: "How I work",
      statements: [
        { title: "I don't need to know everything before I start.", text: "I familiarise myself with processes quickly, ask when information is missing, and then take responsibility for my area." },
        { title: "Once I understand a process, I see the connections.", text: "Including possible problems and workable solutions." },
        { title: "I like working independently.", text: "And just as much within clear structures and specifications." },
      ],
    },
    experience: {
      chapter: "Chapter 05",
      label: "Experience",
      headline: "Professional Experience",
      items: [
        { date: "SINCE 2004", title: "Freelance author & artist", org: "self-employed under Jo Lenz Art since 2026", note: "Development, production and marketing of own art and design products — from idea to sellable result." },
        { date: "UNTIL 01/2014", title: "Project & production management, later shareholder & managing director", org: "Kaiserdisplay", note: "POS, promotion and packaging solutions — client projects, production coordination, procurement, and coordination with suppliers, agencies and production partners." },
        { date: "2013–2020", title: "Set Nanny / Child Supervisor", org: "Film productions", note: "Responsibility for children and families on set, international teams, working under time pressure." },
      ],
      weiterLabel: "Further stations",
      weiter: "Residential childcare assistant at a school for the blind, day nursery, owner of a children's & youth boutique, administration at IG Klettern e. V., Hotel Rheinsberg am See, notary's office.",
      ausbildungLabel: "Education",
      ausbildung: "Vocational training as office management assistant (IHK), Berufsfachschule Königs Wusterhausen — successfully completed.",
    },
    tools: {
      chapter: "Chapter 06",
      label: "Skills",
      headline: "Tools & Know-how",
      list: ["Adobe Illustrator", "Adobe Photoshop", "Adobe InDesign", "Print Preparation / Prepress", "Packaging", "Product Development", "Prototyping", "Finishing", "MS Office", "Canva", "Procreate", "Squarespace"],
    },
    contact: {
      chapter: "Chapter 07",
      label: "Contact",
      headline: "Maybe we can start something together.",
      routes: [
        {
          key: "employer",
          label: "For companies & employers",
          text: "Looking for someone who steers projects independently, understands production and takes responsibility for real tasks?",
          primary: true,
        },
        {
          key: "project",
          label: "For projects",
          text: "Have a product, publication, print or packaging idea that needs someone who can think through the practical implementation?",
          primary: false,
        },
      ],
      emailLabel: "Feel free to write to me:",
      location: "Jo Lenz · Berlin / Brandenburg",
      artLabel: "jolenz.art",
      artNote: "Artistic website — opens in a new tab",
      portraitSrc: "/images/portrait.jpg",
      portraitAlt: "Portrait of Jo Lenz",
    },
    cv: {
      requestLabel: "Request CV",
      title: "Request CV",
      intro: "Leave your name and email address — I'll send you my CV as a PDF. No newsletter, no advertising.",
      nameLabel: "Name",
      emailLabel: "Email address",
      companyLabel: "Company / Organisation",
      optionalNote: "optional",
      submit: "Send request",
      sending: "Sending …",
      successTitle: "Thank you — request received.",
      successText: "I'll get back to you with the CV by email.",
      errorFields: "Please provide your name and a valid email address.",
      errorGeneric: "Something went wrong. Please try again or write directly to work@jolenz.de.",
      close: "Close",
      variantsLabel: "Which CV would you like to receive?",
      variantMain: "Project & Operations — project coordination, production, prepress",
      variantPed: "People & Care — child & family care, education",
      variantOffice: "Customer & Office — customer service, organisation, administration",
    },
    footer: {
      line: "Project Management · Production · Product Development · Prepress · Berlin / Brandenburg",
      copyright: "© 2026 Jo Lenz",
      madeNote: "Berlin / Brandenburg",
      legal: [
        { label: "Imprint", url: "https://jolenz.art/privacy-policy-and-impressum", testid: "footer-impressum-link" },
        { label: "Privacy Policy", url: "https://jolenz.art/privacy-policy-and-impressum", testid: "footer-datenschutz-link" },
      ],
    },
  },
};

// --- Schutz vor alten DB-Ständen (Schema-Mismatch darf die Seite niemals leer rendern) ---
// Ein gespeichertes DB-Dokument wird nur übernommen, wenn es die aktuelle Schema-Struktur enthält.
// Fehlende Felder fallen beim Merge automatisch auf die gebündelten Inhalte zurück.
const REQUIRED_SECTIONS = ["einsatz", "cases", "moreProjects", "howIWork", "experience", "tools", "contact"];

export const isContentCompatible = (doc) =>
  !!(
    doc &&
    REQUIRED_SECTIONS.every((k) => doc.de && doc.de[k] && doc.en && doc.en[k]) &&
    Array.isArray(doc.de.einsatz.areas) &&
    Array.isArray(doc.de.cases.items) &&
    Array.isArray(doc.de.moreProjects.items)
  );

const mergeDeep = (base, over) => {
  if (Array.isArray(base)) return Array.isArray(over) ? over : base;
  if (base && typeof base === "object") {
    const out = { ...base };
    if (over && typeof over === "object" && !Array.isArray(over)) {
      for (const k of Object.keys(base)) {
        if (over[k] !== undefined && over[k] !== null) out[k] = mergeDeep(base[k], over[k]);
      }
    }
    return out;
  }
  return over !== undefined && over !== null ? over : base;
};

// Liefert immer ein vollständiges Dokument: gebündelte Inhalte + darübergelegte gültige DB-Werte.
export const mergeSiteContent = (remote) => ({
  de: mergeDeep(content.de, remote.de),
  en: mergeDeep(content.en, remote.en),
});
